import React, {useState} from 'react';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import StateButton from 'app/general/widgets/StateButton';
import {useCreateTeam} from 'app/teams/api';
import {AddNewTeamMemberButton} from 'app/teams/components/TeamDetailPage/TeamMembersSubPage/AddNewTeamMemberButton';
import {TeamMemberListItem} from 'app/teams/components/TeamDetailPage/TeamMembersSubPage/TeamMemberListItem';
import {useInviteUser} from 'app/user/api';
import {useModal} from 'app/utils/hooks';
import {gt} from 'app/utils/i18n';

import type {MenuItemsType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {SelectedItemType} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import type {NewInvitationType} from 'app/user/types';
import type {TeamSerializedType, UserSerializedType} from 'sweet-types';

type Props<IT, OT> = {
  item: SelectedItemType<IT, OT>;
  onClose: () => unknown;
  show: boolean;
};

export function CreateItemModal({
  item,
  onClose,
  show,
}: Props<string, TeamSerializedType>) {
  const [teamName, setTeamName] = useState(item.object ?? '');
  const [isManager, setIsManager] = useState(false);
  const [users, setUsers] = useState<Array<UserSerializedType>>([]);
  const [createTeam] = useCreateTeam();
  const [inviteUser] = useInviteUser();

  const title = item.title ?? gt('Create Team');
  const icon = item.icon ?? 'far fa-users';
  const description =
    item?.description ??
    gt(
      'A team is a way to control access to your procedures, processes, policies and quizzes (documents)'
    );
  const help = item?.help ?? null;
  function handleSave() {
    return createTeam({name: teamName})
      .unwrap()
      .then((team) => {
        item.action(team);
        return addUsersToTeam(team);
      })
      .then(onClose);
  }

  function selectUsers(users: Array<UserSerializedType>, isManager: boolean) {
    setUsers(users);
    setIsManager(isManager);
    return Promise.resolve(true);
  }

  function addUsersToTeam(team: TeamSerializedType) {
    if (users.length === 0) {
      return Promise.resolve(team);
    }
    const invitations = users.map<NewInvitationType>((user) => ({
      send_mail: false,
      content_type: 'team',
      permission: isManager ? 'edit' : 'view',
      object_id: team.id,
      to_user_id: user.url,
    }));
    return inviteUser({invitations}).unwrap();
  }

  function memberActionsGetter(user: UserSerializedType): MenuItemsType {
    return [
      {
        text: gt('Remove from Team'),
        key: 'remove',
        icon: 'fa fa-times',
        condition: () => true,
        callback: () =>
          setUsers((users) => users.filter((u) => u.url != user.url)),
      },
    ];
  }

  return (
    <TabbedModal
      show={show}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'create-object',
          name: title,
          icon,
          content: (
            <div>
              <p>{description}</p>
              <p className="text-muted">{help}</p>
              <form>
                <div className="form-group">
                  <label htmlFor="teamName">{gt('Team Name')}</label>
                  <input
                    type="text"
                    className="form-control"
                    id="teamName"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                  />
                </div>
              </form>
              <p className="border-bottom mb-1">{gt('Members')}</p>
              <AddNewTeamMemberButton
                title={gt('Add Member')}
                team={{id: 1}}
                onConfirm={selectUsers}
                className="btn btn-link bn-sm pl-0 mb-3"
              />
              <ul className="list-unstyled">
                {users.map((user) => (
                  <TeamMemberListItem
                    key={user.url}
                    object={user}
                    canEdit={true}
                    memberships={[]}
                    permDescription={
                      isManager ? (
                        <small className="mr-1">{gt('Team Manager')}</small>
                      ) : undefined
                    }
                    actionsGetter={memberActionsGetter}
                  />
                ))}
              </ul>
              <StyledModalFooter>
                <button
                  onClick={onClose}
                  className="btn btn-link"
                  data-dismiss="modal"
                >
                  <i className="fas fa-times mr-2" />
                  {gt('Cancel')}
                </button>
                <StateButton
                  disabled={!item || teamName === ''}
                  onClick={handleSave}
                  busyText={gt('Creating...')}
                  className="btn btn-primary"
                  data-dismiss="modal"
                >
                  {title}
                </StateButton>
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
}

export function useCreateTeamModal() {
  return useModal<SelectedItemType<string, TeamSerializedType>>({
    Modal: CreateItemModal,
  });
}
